import type { RepresentativeInvitationPublicView } from "@closer/shared";
import { maskEmail } from "@closer/shared";
import { Button, Card, Spinner, useToast } from "@closer/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { ISTENEN_DONUS_PARAM } from "../../lib/returnTo";

/**
 * Müvekkil Temsilcisi davet ekranı (CLOS-177 `[124]` — rota borcu).
 *
 * Davet e-postası `/temsilci-davet?token=...` adresine yönlendiriyordu; uçlar `[124]`'te
 * hazırdı ama rota CLOS-227'ye ertelenmişti. linkGuard kapısı haklı olarak kırmızıya
 * düştü: rotasız bağlantı catch-all'a düşer ve token kaybolur. Ekran, kapının istediği
 * asgari halka olarak burada açılıyor; "Davetlerim" merkezi yine CLOS-227'de.
 *
 * ### Kabul burada YÖNLENDİRMEZ
 *
 * Katılımcı davetinden (CLOS-147) ayrılan nokta: temsilcinin kabulü YETKİ DEĞİLDİR —
 * erişim ancak sponsorun aktivasyonuyla (CLOS-178) doğar. Kabul sonrası gidilecek bir
 * yüzey henüz yoktur; ekran bunu saklamak yerine açıkça "aktivasyon bekleniyor" der.
 */

type Durum = "yukleniyor" | "hazir" | "gecersiz" | "islemde" | "kabul" | "reddedildi";

/**
 * Ekranın kendi bekleme süresi.
 *
 * ⚠ SUNUCUNUN COOLDOWN'I DEĞİL, ONUN GÖLGESİ: gerçek sınır `EMAIL_VERIFICATION_RESEND_
 * COOLDOWN_SECONDS` ile sunucuda uygulanıyor ve cevabı bilerek ayırt etmiyor. Buradaki
 * sayı kullanıcıya "bir şey oldu mu" belirsizliği yaşatmamak için; kapı değil.
 */
const DOGRULAMA_BEKLEME_SANIYE = 60;

export function RepresentativeInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout } = useAuth();

  const [davet, setDavet] = useState<RepresentativeInvitationPublicView | null>(null);
  const [durum, setDurum] = useState<Durum>(token ? "yukleniyor" : "gecersiz");
  const [hata, setHata] = useState<string | null>(null);

  /**
   * DOĞRULAMA GÖNDERİMİ (FAZ 0A).
   *
   * ⚠ COOLDOWN EKRANDA DA VAR ve bu bir tekrar değil: sunucu cooldown'ı zaten uyguluyor
   * ama cevabı BİLEREK ayırt etmiyor (anti-enumeration — "gönderildi" ile "bekle" aynı
   * mesaj). Yani istemci sunucunun cevabından bekleme süresini öğrenemez; öğrenseydi uç
   * bir hesap-varlığı sorgulama aracına dönerdi. Ekran bu yüzden KENDİ sayacını tutuyor:
   * kullanıcıya "bir şey oldu mu" belirsizliği yaşatmamak için, kapı olarak değil.
   */
  const [gonderimDurumu, setGonderimDurumu] = useState<"hazir" | "gonderiliyor" | "gonderildi">(
    "hazir",
  );
  const [bekleyenSaniye, setBekleyenSaniye] = useState(0);
  const sayacRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (sayacRef.current) clearInterval(sayacRef.current);
    };
  }, []);

  async function dogrulamaGonder(): Promise<void> {
    if (gonderimDurumu === "gonderiliyor" || bekleyenSaniye > 0) return;
    setGonderimDurumu("gonderiliyor");
    try {
      /**
       * ⚠ GÖVDEDE ADRES YOK: oturum varken sunucu adresi KENDİ belirliyor (CLOS-822).
       * Buradan adres göndermek, gövdeyi değiştiren bir kullanıcının istediği adrese
       * ürünün adıyla e-posta tetiklemesine kapı açardı.
       */
      await api("/api/session/resend-verification", {
        method: "POST",
        body: JSON.stringify({}),
      });
      setGonderimDurumu("gonderildi");
      setBekleyenSaniye(DOGRULAMA_BEKLEME_SANIYE);
      sayacRef.current = setInterval(() => {
        setBekleyenSaniye((onceki) => {
          if (onceki <= 1 && sayacRef.current) clearInterval(sayacRef.current);
          return Math.max(0, onceki - 1);
        });
      }, 1000);
    } catch (err) {
      setGonderimDurumu("hazir");
      toast.error(
        err instanceof ApiRequestError ? err.message : "Doğrulama e-postası gönderilemedi",
      );
    }
  }

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const { data } = await api<RepresentativeInvitationPublicView>(
          `/api/representative-invitations?token=${encodeURIComponent(token)}`,
        );
        setDavet(data);
        setDurum("hazir");
      } catch {
        // Var olmayan/dolmuş/iptal edilmiş davet sunucuda aynı cevaba düşüyor; ekran da
        // ayırt ediyormuş gibi davranmıyor — atılacak adım hepsinde aynı.
        setDurum("gecersiz");
      }
    })();
  }, [token]);

  async function yanitla(karar: "accept" | "reject") {
    if (!token) return;
    setDurum("islemde");
    setHata(null);
    try {
      await api(`/api/representative-invitations/${karar}`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setDurum(karar === "accept" ? "kabul" : "reddedildi");
    } catch (error) {
      setHata(
        error instanceof ApiRequestError
          ? error.message
          : "Davet yanıtlanamadı. Lütfen tekrar deneyin.",
      );
      setDurum("hazir");
    }
  }

  const hesapDegistir = async (): Promise<void> => {
    try {
      await logout();
      navigate(`/login?temsilci=${encodeURIComponent(token ?? "")}`);
    } catch {
      toast.error("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <Card title="Temsilcilik Daveti" className="w-full max-w-sm">
        {durum === "yukleniyor" && (
          <div role="status" className="flex flex-col items-center gap-3 py-4">
            <Spinner size="lg" />
            <p className="text-sm text-fg-muted">Davet kontrol ediliyor…</p>
          </div>
        )}

        {durum === "gecersiz" && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Bu davet geçerli değil. Bağlantının süresi dolmuş, davet iptal edilmiş ya da daha önce
              yanıtlanmış olabilir. Sizi davet eden kişiden yeni bir davet isteyebilirsiniz.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {durum === "kabul" && davet && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Daveti kabul ettiniz. <strong>{davet.clientDisplayName}</strong> adına temsilciliğiniz
              için <strong>{davet.organizationName}</strong> tarafının aktifleştirmesi bekleniyor;
              erişim ancak o adımla açılır. Sonuç size e-postayla bildirilecek.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {durum === "reddedildi" && davet && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Daveti reddettiniz. <strong>{davet.clientDisplayName}</strong> adına temsilcilik
              erişimi oluşmadı.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {(durum === "hazir" || durum === "islemde") && davet && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-fg-muted">
                <strong className="text-fg">{davet.organizationName}</strong>, sizi{" "}
                <strong className="text-fg">{davet.clientDisplayName}</strong> adına Müvekkil
                Temsilcisi olarak davet etti.
              </p>
              <p className="text-sm text-fg-soft">Önerilen kapsam: {davet.proposedScope}</p>
            </div>

            {!user?.email ? (
              <>
                <p className="text-sm text-fg-muted">
                  Daveti yanıtlamak için <strong>{davet.email}</strong> adresiyle giriş yapın. Bu
                  adresle hesabınız yoksa önce kayıt olun; işlem bitince bu sayfaya geri dönersiniz.
                </p>
                <Link
                  to={`/login?temsilci=${encodeURIComponent(token ?? "")}`}
                  className="text-center font-medium text-primary hover:underline"
                >
                  Giriş yap
                </Link>
                <Link
                  to={`/register?temsilci=${encodeURIComponent(token ?? "")}`}
                  className="text-center font-medium text-primary hover:underline"
                >
                  Kayıt ol
                </Link>
              </>
            ) : (
              <>
                <p role="status" className="text-sm text-fg-muted">
                  Bu davet <strong>{davet.email}</strong> adresine gönderildi; yanıtlamak için o
                  hesapla giriş yapmış olmalısınız. Şu an <strong>{user.email}</strong> ile
                  giriştesiniz.
                </p>

                {/*
                  ⚠ ÖLÜ UÇ KAPANIYOR (FAZ 0A): doğrulanmamış hesap "önce doğrulayın"
                  hatasını görüyor ama yapabileceği hiçbir şey sunulmuyordu. Kapı
                  sunucuda ve doğru; eksik olan çıkış yoluydu.
                */}
                {!user.emailVerified && (
                  <div className="flex flex-col gap-2 rounded-md border border-border-default p-3">
                    <p className="text-sm text-fg">
                      Daveti yanıtlamak için önce e-posta adresinizi doğrulamanız gerekiyor.
                    </p>
                    {/* ⚠ Adres MASKELİ: kullanıcı hangi kutuya bakacağını bilmeli, ekran
                        da tam adresi gereksiz yere tekrarlamamalı. */}
                    <p className="text-xs text-fg-soft">
                      Doğrulama bağlantısı {maskEmail(user.email)} adresine gönderilir.
                    </p>

                    {gonderimDurumu === "gonderildi" && (
                      <p role="status" className="text-xs text-fg-soft">
                        Doğrulama bağlantısı gönderildi. Gelen kutunuzu kontrol edin.
                      </p>
                    )}

                    <Button
                      size="sm"
                      onClick={() => void dogrulamaGonder()}
                      loading={gonderimDurumu === "gonderiliyor"}
                      disabled={bekleyenSaniye > 0}
                    >
                      {gonderimDurumu === "gonderildi"
                        ? `Yeniden gönder${bekleyenSaniye > 0 ? ` (${bekleyenSaniye})` : ""}`
                        : "Doğrulama e-postası gönder"}
                    </Button>

                    {/*
                      ⚠ DÖNÜŞ HEDEFİ TOKEN'I TAŞIYOR: doğrulama sayfasına giderken davetin
                      kimliği kaybolursa kullanıcı doğrulasa bile davete dönemez. Hedef
                      karşı tarafta izin listesinden geçiyor (`guvenliDonusYolu`).
                    */}
                    <Link
                      to={`/verify-email?${ISTENEN_DONUS_PARAM}=${encodeURIComponent(
                        `/temsilci-davet?token=${token ?? ""}`,
                      )}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      E-posta doğrulama sayfasına git
                    </Link>
                  </div>
                )}

                {hata && (
                  <p role="alert" className="text-sm text-danger">
                    {hata}
                  </p>
                )}

                {/*
                  ⚠ KABUL DÜĞMESİ DOĞRULANMAMIŞ HESAPTA KAPALI: sunucu zaten reddediyor,
                  ekran yapılamayacak bir şeyi sunmuyor. Kapı yine sunucuda.
                */}
                <Button
                  onClick={() => void yanitla("accept")}
                  loading={durum === "islemde"}
                  disabled={!user.emailVerified}
                >
                  Daveti kabul et
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void yanitla("reject")}
                  disabled={durum === "islemde" || !user.emailVerified}
                >
                  Reddet
                </Button>

                <p className="text-center text-sm text-fg-soft">
                  Bu hesap değil mi?{" "}
                  <Button
                    variant="ghost"
                    onClick={() => void hesapDegistir()}
                    disabled={durum === "islemde"}
                  >
                    Farklı hesapla giriş yap
                  </Button>
                </p>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
